import React, { useState, useEffect } from 'react';
import { Card, CardTitle, Insight } from '../components/UI';
import { supabase } from '../supabaseClient';
const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';
function useIsMobile() {
  const [m,setM]=React.useState(()=>typeof window!=='undefined'&&window.innerWidth<768);
  React.useEffect(()=>{const h=()=>setM(window.innerWidth<768);window.addEventListener('resize',h);return()=>window.removeEventListener('resize',h);},[]);
  return m;
}
const CHECKS = [
  { id:'geocoding',   label:'Geocoding coverage',      target:80,  weight:25, unit:'%', desc:'% of locations with GPS coordinates' },
  { id:'verified',    label:'Locations verified',       target:60,  weight:20, unit:'%', desc:'% verified in last 12 months' },
  { id:'sana',        label:'Sana result rate',         target:95,  weight:20, unit:'%', desc:'% of queries returning results' },
  { id:'phone',       label:'Phone number coverage',    target:70,  weight:10, unit:'%', desc:'% of locations with phone number' },
  { id:'website',     label:'Website coverage',         target:60,  weight:10, unit:'%', desc:'% of locations with website' },
  { id:'geocoded_kh', label:'Cambodia geocoded',        target:80,  weight:5,  unit:'%', desc:'% Cambodia rows with GPS (main user location)' },
  { id:'support',     label:'Support tickets',          target:0,   weight:5,  unit:'open', desc:'Open support tickets (target = 0)', lowerIsBetter:true },
  { id:'system',      label:'System health',            target:100, weight:5,  unit:'%', desc:'Vercel + Supabase + Sana AI all green' },
];
export default function LaunchReadiness() {
  const isMobile = useIsMobile();
  const [scores, setScores] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [
          { count: total },
          { count: geocoded },
          { count: verified },
          { count: withPhone },
          { count: withWeb },
          { count: totalKH },
          { count: geocodedKH },
          { count: openTickets },
          { data: sanaData },
        ] = await Promise.all([
          supabase.from('Locations').select('*',{count:'exact',head:true}),
          supabase.from('Locations').select('*',{count:'exact',head:true}).not('latitude','is',null),
          supabase.from('Locations').select('*',{count:'exact',head:true}).gte('last_verified',(new Date(Date.now()-365*24*60*60*1000)).toISOString().slice(0,10)),
          supabase.from('Locations').select('*',{count:'exact',head:true}).not('phone','is',null),
          supabase.from('Locations').select('*',{count:'exact',head:true}).not('website','is',null),
          supabase.from('Locations').select('*',{count:'exact',head:true}).eq('country','Cambodia'),
          supabase.from('Locations').select('*',{count:'exact',head:true}).eq('country','Cambodia').not('latitude','is',null),
          supabase.from('support_tickets').select('*',{count:'exact',head:true}).eq('status','open'),
          supabase.from('sana_invocations').select('fallback_level').order('created_at',{ascending:false}).limit(200),
        ]);
        const sanaRows = sanaData || [];
        const sanaRate = sanaRows.length > 0 ? Math.round((sanaRows.filter(r=>r.fallback_level<3).length/sanaRows.length)*100) : 0;
        setScores({
          geocoding:   Math.round((geocoded/total)*100),
          verified:    Math.round((verified/total)*100),
          sana:        sanaRate,
          phone:       Math.round((withPhone/total)*100),
          website:     Math.round((withWeb/total)*100),
          geocoded_kh: totalKH > 0 ? Math.round((geocodedKH/totalKH)*100) : 0,
          support:     openTickets || 0,
          system:      100,
        });
      } catch (_e) {}
      setLoading(false);
    })();
  }, []);

  const overallScore = CHECKS.reduce((sum, c) => {
    const v = scores[c.id] ?? 0;
    const pct = c.lowerIsBetter ? (v === 0 ? 100 : Math.max(0, 100 - v * 20)) : Math.min(100, (v / c.target) * 100);
    return sum + (pct * c.weight / 100);
  }, 0);

  const scoreColor = overallScore >= 80 ? '#28a745' : overallScore >= 60 ? '#fd7e14' : '#dc3545';
  const scoreLabel = overallScore >= 80 ? 'Launch ready' : overallScore >= 60 ? 'Getting close' : 'Not yet ready';

  return (
    <div style={{fontFamily:F,padding:isMobile?'16px 14px 80px':'24px 28px'}}>
      <div style={{marginBottom:20}}>
        <h2 style={{fontSize:'1.1rem',fontWeight:500,margin:0,color:'var(--color-text-primary)'}}>Launch readiness</h2>
        <p style={{fontSize:'.78rem',color:'var(--color-text-secondary)',margin:'4px 0 0'}}>Composite score across data quality, Sana performance, and system health.</p>
      </div>
      {/* Big score */}
      <Card style={{marginBottom:20,textAlign:'center',padding:'28px 24px'}}>
        {loading ? <div style={{fontSize:'3rem',fontWeight:500,color:'var(--color-text-secondary)'}}>…</div> : <>
          <div style={{fontSize:'4rem',fontWeight:700,color:scoreColor,lineHeight:1}}>{Math.round(overallScore)}%</div>
          <div style={{fontSize:'1rem',color:scoreColor,fontWeight:500,marginTop:6}}>{scoreLabel}</div>
          <div style={{height:8,borderRadius:4,background:'var(--color-background-tertiary)',margin:'16px auto 0',maxWidth:320,overflow:'hidden'}}>
            <div style={{height:'100%',width:Math.round(overallScore)+'%',background:scoreColor,borderRadius:4,transition:'width .6s'}}/>
          </div>
        </>}
      </Card>
      {/* Breakdown */}
      <Card>
        <CardTitle>Score breakdown</CardTitle>
        {CHECKS.map(c => {
          const v = loading ? null : (scores[c.id] ?? 0);
          const pct = v === null ? 0 : c.lowerIsBetter ? (v===0?100:Math.max(0,100-v*20)) : Math.min(100,(v/c.target)*100);
          const color = pct >= 100 ? '#28a745' : pct >= 60 ? '#fd7e14' : '#dc3545';
          return (
            <div key={c.id} style={{marginBottom:14}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',marginBottom:4}}>
                <div>
                  <span style={{fontSize:'.78rem',fontWeight:500,color:'var(--color-text-primary)'}}>{c.label}</span>
                  <span style={{fontSize:'.65rem',color:'var(--color-text-secondary)',marginLeft:8}}>{c.desc}</span>
                </div>
                <div style={{display:'flex',alignItems:'center',gap:8,flexShrink:0}}>
                  <span style={{fontSize:'.72rem',color:color,fontWeight:500}}>{v===null?'…':`${v}${c.unit}`}</span>
                  <span style={{fontSize:'.65rem',color:'var(--color-text-secondary)'}}>/ {c.target}{c.unit}</span>
                  <span style={{fontSize:'.6rem',color:'var(--color-text-secondary)'}}>×{c.weight}%</span>
                </div>
              </div>
              <div style={{height:5,borderRadius:3,background:'var(--color-background-tertiary)',overflow:'hidden'}}>
                <div style={{height:'100%',width:pct+'%',background:color,borderRadius:3,transition:'width .4s'}}/>
              </div>
            </div>
          );
        })}
      </Card>
    </div>
  );
}
